import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight, ChevronRight } from 'lucide-react';
import { cn } from '../../ui/cn';
import { Skeleton } from '../../ui/Skeleton';

/**
 * The pieces the executive dashboard is built from.
 *
 * Split out of the page so the page file reads as LAYOUT — what sits where, and why — instead of
 * eight hundred lines in which the shape of a KPI tile and the shape of the screen are tangled
 * together. Nothing here knows about orders, money or permissions; they take data and draw it.
 */

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Sparkline
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * A bare inline SVG, not a charting library.
 *
 * A sparkline has no axes, no grid, no tooltip and no legend — it is a shape, and the whole point is
 * that it costs nothing. Rendering eight of them through Recharts would mount eight
 * ResponsiveContainers with their own resize observers to draw eight polylines.
 *
 * It is drawn ONLY where a real series exists. The temptation with a KPI row is to give every tile a
 * squiggle for visual consistency, which means inventing data for the tiles that have none — so the
 * tiles without a series simply have no sparkline, and the row stays honest.
 */
export function Spark({ data, tone = 'brand', className }: {
  data: number[]; tone?: 'brand' | 'danger' | 'sky' | 'violet' | 'gold' | 'muted'; className?: string;
}) {
  if (!data || data.length < 2) return null;
  const W = 72, H = 26, P = 2;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const span = max - min || 1;
  const x = (i: number) => P + (i * (W - P * 2)) / (data.length - 1);
  const y = (v: number) => H - P - ((v - min) / span) * (H - P * 2);
  const line = data.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L${x(data.length - 1).toFixed(1)},${H} L${x(0).toFixed(1)},${H} Z`;
  const STROKE = {
    brand: 'text-brand-500', danger: 'text-danger-500', sky: 'text-sky-500',
    violet: 'text-plum-500', gold: 'text-harvest-400', muted: 'text-line-strong',
  }[tone];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className={cn('shrink-0 overflow-visible', STROKE, className)} aria-hidden focusable="false">
      <path d={area} fill="currentColor" opacity={0.12} />
      <path d={line} fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Delta
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * "▲ 12% vs last period".
 *
 * `goodWhenUp` exists because a rising number is not automatically good news: revenue climbing is
 * green, money owed to us climbing is not. Tying the colour to the metric's own direction rather
 * than to the arrow keeps red meaning "worse" everywhere on the page.
 *
 * Renders nothing when there is no comparable previous figure — an unexplained "0%" reads as a
 * measurement, and a fabricated one is worse.
 */
export function Delta({ pct, goodWhenUp = true, label = 'vs last period' }: {
  pct: number | null; goodWhenUp?: boolean; label?: string;
}) {
  if (pct === null || !Number.isFinite(pct)) return null;
  const up = pct > 0;
  const flat = Math.abs(pct) < 0.5;
  const good = flat ? null : up === goodWhenUp;
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-[11px] font-semibold',
      good === null ? 'text-subtle' : good ? 'text-brand-600 dark:text-brand-400' : 'text-danger-600 dark:text-danger-500')}>
      {!flat && (up ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />)}
      {flat ? 'no change' : `${Math.abs(Math.round(pct))}%`}
      <span className="font-normal text-subtle"> {label}</span>
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * KPI tile
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

export type KpiTone = 'brand' | 'sky' | 'gold' | 'danger' | 'violet' | 'slate';

/**
 * Per-metric colour, applied to the whole tile — not just the icon.
 *
 * `card` is a WASH: a few percent of the metric's own hue behind the tile plus a matching hairline.
 * It is what turns eight identical white rectangles into a row you can navigate by colour, and it is
 * why the reference design reads as a dashboard while a plain white row reads as a table. Kept to a
 * wash rather than a fill so the number, not the box, is still the loudest thing on the tile.
 *
 * `spark` is the stroke colour, so a tile's sparkline belongs to the same family as its chip.
 */
const KPI_TONE: Record<KpiTone, { chip: string; card: string; spark: 'brand' | 'danger' | 'sky' | 'violet' | 'gold' | 'muted' }> = {
  brand: {
    chip: 'bg-brand-100 text-brand-700 dark:bg-brand-400/20 dark:text-brand-300',
    card: 'border-brand-500/25 bg-brand-500/[0.045] dark:bg-brand-500/[0.07]',
    spark: 'brand',
  },
  sky: {
    chip: 'bg-sky-400/15 text-sky-600 dark:bg-sky-400/20 dark:text-sky-300',
    card: 'border-sky-500/25 bg-sky-500/[0.045] dark:bg-sky-500/[0.07]',
    spark: 'sky',
  },
  gold: {
    chip: 'bg-harvest-100 text-harvest-700 dark:bg-harvest-400/20 dark:text-harvest-300',
    card: 'border-harvest-400/35 bg-harvest-400/[0.07] dark:bg-harvest-400/[0.09]',
    spark: 'gold',
  },
  danger: {
    chip: 'bg-danger-500/12 text-danger-600 dark:bg-danger-500/20 dark:text-danger-400',
    card: 'border-danger-500/25 bg-danger-500/[0.045] dark:bg-danger-500/[0.07]',
    spark: 'danger',
  },
  violet: {
    chip: 'bg-plum-400/15 text-plum-600 dark:bg-plum-400/20 dark:text-plum-300',
    card: 'border-plum-400/25 bg-plum-400/[0.05] dark:bg-plum-400/[0.08]',
    spark: 'violet',
  },
  slate: {
    chip: 'bg-surface-3 text-muted',
    // Even the uncoloured tone gets a wash — of the canvas's own green-grey, not of a hue. Left
    // plain white it was the ONE bare rectangle in a tinted band, which reads as a tile that failed
    // to load rather than as a tile with no category.
    card: 'border-line bg-surface-2/70 dark:bg-surface-2/60',
    spark: 'muted',
  },
};

/**
 * One KPI: icon, label, figure, and — where the data supports it — a movement and a shape.
 *
 * The whole tile is a button. Every headline number on a dashboard is a question ("why is that so
 * low?"), and the answer is always a list somewhere; a tile that cannot be opened makes the reader
 * go and find that list by hand.
 *
 * `active` is for the case where the tile does not navigate away but FILTERS the list below it: the
 * band becomes the page's filter control, and the pressed tile has to say so or the reader is left
 * looking at a narrowed table with no visible reason for it. Marked with `aria-pressed` as well as a
 * ring, since "which one is on" is exactly the fact a screen reader otherwise loses.
 */
export function Kpi({
  icon, tone, label, value, sub, delta, spark, onClick, active, loading,
}: {
  icon: ReactNode; tone: KpiTone; label: string; value: string;
  sub?: ReactNode; delta?: ReactNode; spark?: number[];
  onClick?: () => void; active?: boolean; loading?: boolean;
}) {
  const t = KPI_TONE[tone];
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      {...(onClick ? { type: 'button' as const, onClick, ...(active !== undefined ? { 'aria-pressed': active } : {}) } : {})}
      className={cn(
        'group relative flex min-w-0 flex-col overflow-hidden rounded-card border px-4 py-3.5 text-left shadow-e1',
        t.card,
        // Hover RAISES the tile (never moves it). `hover:` carries pseudo-class specificity, so it
        // beats the resting `shadow-e1` whatever order Tailwind emits the two rules in.
        onClick && 'transition-shadow hover:shadow-e2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        // Ring INSIDE the tile (not a wider outline) so a pressed tile doesn't shove its neighbours.
        active && 'ring-2 ring-inset ring-content/45',
      )}
    >
      <span className="flex items-center gap-2">
        <span className={cn('grid size-8 shrink-0 place-items-center rounded-lg [&>svg]:size-4', t.chip)} aria-hidden>
          {icon}
        </span>
        {/* Two lines rather than an ellipsis: "Money Owed to Us" and "Open Complaints" were both
            being cut on a phone, and half a tile's name does not say what the number is. */}
        <span className="line-clamp-2 text-xs font-medium leading-tight text-muted">{label}</span>
      </span>

      {/* THE FIGURE TAKES THE WIDTH IT NEEDS; THE SPARKLINE GIVES WAY.
          It was the other way round — the value was `min-w-0 truncate` next to a fixed 72px squiggle
          that could not shrink — so "₹3.48 L" (93px at this type size) was handed 68px and the Revenue
          tile printed a cut-off number on every layout narrower than about 1500px. The sparkline is
          decoration: it scales down to whatever is left over, and the number is never cut. */}
      <span className="mt-2.5 flex items-end justify-between gap-2">
        <span className="shrink-0 whitespace-nowrap text-[27px] font-bold leading-none tracking-[-0.025em] tabular-nums text-content">
          {loading ? <Skeleton className="h-7 w-20" /> : value}
        </span>
        {spark && spark.length > 1 && (
          <span className="flex min-w-0 flex-1 justify-end">
            <Spark data={spark} tone={t.spark} className="h-[26px] w-full max-w-[72px]" />
          </span>
        )}
      </span>

      {/* The hint WRAPS rather than truncating: "1st of the month to today" and "value across all
          godowns" were both being chopped, and a hint nobody can finish reading is not a hint. */}
      <span className="mt-2 block min-h-4 break-words">
        {delta ?? (sub ? <span className="text-[11px] text-muted">{sub}</span> : null)}
      </span>
    </Tag>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Panel
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * The one card shape on this page: hairline border, the resting step of the elevation ladder, and a
 * tinted header rule.
 *
 * Written once because the previous version of this screen had four different card treatments —
 * border+shadow, border+shadow+rail, bare div, and the ui `Card` — and the page read as four
 * unrelated designs sharing a scroll.
 */
export function Panel({ title, icon, action, children, className, bodyClassName }: {
  title: ReactNode; icon?: ReactNode; action?: ReactNode;
  children: ReactNode; className?: string; bodyClassName?: string;
}) {
  return (
    <section className={cn('flex min-w-0 flex-col rounded-card border border-line bg-surface shadow-e1', className)}>
      {/* Same treatment as ui/Card's header — a whisper of brand green under the title, cut to the
          panel's own radius minus its 1px border so the tint follows the rounded corner. */}
      <header className="flex items-center justify-between gap-3 rounded-t-[calc(var(--radius-card)_-_1px)] border-b border-line bg-linear-to-b from-brand-500/[0.06] to-transparent px-4 py-3">
        <h2 className="flex min-w-0 items-center gap-2 text-sm font-semibold text-content">
          {icon && <span className="shrink-0 text-brand-600 [&>svg]:size-4" aria-hidden>{icon}</span>}
          <span className="truncate">{title}</span>
        </h2>
        {action && <div className="shrink-0">{action}</div>}
      </header>
      <div className={cn('flex-1 p-4', bodyClassName)}>{children}</div>
    </section>
  );
}

/** A quiet "View all →" that belongs in a panel header without competing with the title. */
export function PanelLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button" onClick={onClick}
      className="group inline-flex items-center gap-0.5 rounded-control border border-line px-2 py-1 text-xs font-medium text-muted transition-colors hover:border-brand-300 hover:text-content"
    >
      {label}
      <ChevronRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

/**
 * An empty state that says WHY it is empty.
 *
 * "No data" on a page that also says "192 orders" is not a fact, it is a riddle — and to most
 * readers it looks like the screen failed to load.
 */
export function Empty({ title, detail, action, className }: {
  title: string; detail?: string; action?: ReactNode; className?: string;
}) {
  return (
    <div className={cn('flex h-full flex-col items-center justify-center gap-1.5 px-4 py-8 text-center', className)}>
      <p className="text-sm font-medium text-muted">{title}</p>
      {detail && <p className="max-w-xs text-xs text-subtle">{detail}</p>}
      {action}
    </div>
  );
}
